import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Maximize2, Mic2 } from 'lucide-react';
import { StageBackground } from '../../components/karaoke/StageBackground';
import { BACKEND, kApi } from '../../lib/karaokeApi';

const GLOW = { purple_stage: '#d946ef', blue_concert: '#22d3ee', golden_spotlight: '#f59e0b' };

let ytReady;
const loadYT = () => {
  if (ytReady) return ytReady;
  ytReady = new Promise((resolve) => {
    if (window.YT?.Player) return resolve(window.YT);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(s);
  });
  return ytReady;
};

// Performance video: reports ENDED only when the player itself reaches the end.
const KaraokeVideo = ({ item, nonce }) => {
  const host = useRef(null);
  useEffect(() => {
    let player;
    let done = false;
    loadYT().then((YT) => {
      if (!host.current) return;
      player = new YT.Player(host.current, {
        videoId: item.video_id, width: '100%', height: '100%',
        playerVars: { autoplay: 1, controls: 0, rel: 0, modestbranding: 1, playsinline: 1, iv_load_policy: 3, fs: 0 },
        events: {
          onReady: (e) => { e.target.unMute(); e.target.setVolume(100); e.target.playVideo(); },
          onStateChange: (e) => { if (e.data === 0 && !done) { done = true; kApi.ended(item.id, nonce).catch(() => {}); } },
          onError: (e) => { if (!done) { done = true; kApi.error(item.id, e.data).catch(() => {}); } },
        },
      });
    });
    return () => { done = true; try { player?.destroy(); } catch { /* noop */ } };
  }, [item.id, item.video_id, nonce]);
  return <div className="absolute inset-0 bg-black" data-testid="karaoke-projector-video"><div ref={host} className="w-full h-full" /></div>;
};

const BetweenSongs = () => (
  <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-[6vw] kin" data-testid="karaoke-projector-between">
    <p className="tracking-[0.5em] text-[1.6vw] font-semibold text-white/80">IC.FUNFLOW PRESENTS</p>
    <h1 className="ktitle mt-[2vh] text-[9vw] leading-none font-black tracking-tight" style={{ fontFamily: '"Bebas Neue", "Oswald", Impact, sans-serif' }}>KARAOKE NIGHT</h1>
    <p className="mt-[3vh] text-[2.2vw] font-medium text-white/90 tracking-wide">Your Stage · Your Song · Your Moment</p>
  </div>
);

const NextSinger = ({ next }) => (
  <div key={next?.id} className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-[6vw] kin" data-testid="karaoke-projector-next">
    <p className="tracking-[0.5em] text-[1.8vw] font-semibold text-white/80">NEXT SINGER</p>
    {next ? (
      <>
        <h1 className="ktitle mt-[2vh] text-[8vw] leading-none font-black" style={{ fontFamily: '"Bebas Neue", "Oswald", Impact, sans-serif' }}>{next.singer || 'Our next star'}</h1>
        <p className="mt-[4vh] text-[3vw] font-bold max-w-[80vw] break-words">{next.title || 'Karaoke song'}</p>
        {next.artist && <p className="mt-[1vh] text-[2vw] text-white/80">{next.artist}</p>}
      </>
    ) : <h1 className="mt-[3vh] text-[5vw] font-black">Get ready…</h1>}
  </div>
);

export default function KaraokeProjector() {
  const [d, setD] = useState(null);
  const [armed, setArmed] = useState(false);
  const audio = useRef(null);

  const poll = useCallback(() => kApi.display().then(setD).catch(() => {}), []);
  useEffect(() => { poll(); const t = setInterval(poll, 1500); return () => clearInterval(t); }, [poll]);

  const performing = d?.mode === 'performance' && d?.now_playing;
  const src = d?.music?.[d?.theme] ? `${BACKEND}${d.music[d.theme]}` : null;
  const wantMusic = armed && !!src && !performing && d?.music_playing && !d?.muted;

  useEffect(() => {
    const a = audio.current;
    if (!a) return;
    a.volume = Math.max(0, Math.min(1, (d?.volume ?? 60) / 100));
    if (wantMusic) a.play().catch(() => {}); else a.pause();
  }, [wantMusic, d?.volume, src]);

  const arm = () => {
    setArmed(true);
    document.documentElement.requestFullscreen?.().catch(() => {});
  };

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center overflow-hidden select-none" style={{ cursor: armed ? 'none' : 'default' }}>
      <div className="relative w-screen h-[56.25vw] max-h-screen max-w-[177.78vh] overflow-hidden" style={{ '--kglow': GLOW[d?.theme] || '#d946ef' }} data-testid="karaoke-projector">
        {!performing && <StageBackground theme={d?.theme || 'purple_stage'} />}
        {performing ? <KaraokeVideo item={d.now_playing} nonce={d.play_nonce} />
          : d?.mode === 'next' ? <NextSinger next={d.next} /> : <BetweenSongs />}
        {src && <audio ref={audio} src={src} loop preload="auto" />}
        {!armed && (
          <button type="button" onClick={arm} className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 bg-black/70 text-white" data-testid="karaoke-projector-start">
            <Mic2 className="w-16 h-16" />
            <span className="text-3xl font-bold">Start theatre display</span>
            <span className="text-white/70 flex items-center gap-2"><Maximize2 className="w-4 h-4" /> Tap once to go fullscreen and allow sound</span>
          </button>
        )}
      </div>
    </div>
  );
}
